/**
 * 线上渲染器加载器
 *
 * 约定：线上脚本加载完成后，把渲染器注册到下面任一位置（推荐第一种）：
 *
 *   window.__MARKDOWN_RENDERERS__ = window.__MARKDOWN_RENDERERS__ || {};
 *   window.__MARKDOWN_RENDERERS__["default"] = {
 *     render(markdown, React) {
 *       return React.createElement("div", { className: "my-md" }, markdown);
 *     },
 *   };
 *
 * 或者直接挂全局：
 *   window.MarkdownRenderer = { render(markdown, React) { ... } };
 *
 * render(markdown, React) 必须返回 ReactNode；样式由脚本自己注入（<style> / <link>），
 * 这样「线上脚本」本身就是一个自带样式的完整渲染器。
 *
 * 安全：这里只是把 <script> 插进页面，等于执行第三方代码。只允许 https、同源或本机地址；
 * 真正的可信来源控制要靠部署侧的白名单 / CSP，不要把这个地址做成用户可填的输入。
 *
 * 完整脚本契约（自带样式 / 复用项目样式两种写法、类名表、示例）见 ./README.md。
 */

/** 渲染器注册表挂在 window 上的 key */
export const RENDERER_REGISTRY_KEY = "__MARKDOWN_RENDERERS__";

/** 单个脚本的加载超时（毫秒） */
const SCRIPT_LOAD_TIMEOUT = 15000;

/** 同一个 URL 只加载一次；失败会从缓存里移除，允许重试 */
const scriptTasks = new Map();

/**
 * 只允许 https / 同源 / localhost（本地调试）。
 * 返回规范化后的绝对地址；不合法返回 null。
 */
export function resolveScriptUrl(url) {
  if (typeof window === "undefined") return null;
  const raw = String(url || "").trim();
  if (!raw) return null;

  let parsed;
  try {
    parsed = new URL(raw, window.location.href);
  } catch (err) {
    return null;
  }

  const sameOrigin = parsed.origin === window.location.origin;
  const isHttps = parsed.protocol === "https:";
  const isLocalHttp =
    parsed.protocol === "http:" &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/i.test(parsed.hostname);

  return isHttps || sameOrigin || isLocalHttp ? parsed.href : null;
}

/** 把函数 / 对象统一成 { render } 结构；不合法返回 null */
export function normalizeRenderer(renderer) {
  if (!renderer) return null;
  if (typeof renderer === "function") return { render: renderer };
  if (typeof renderer.render === "function") return renderer;
  return null;
}

/**
 * 从 window 上找已注册的渲染器。
 * 依次尝试：注册表里指定 key → 注册表 default → window.MarkdownRenderer
 */
export function resolveRendererPlugin(id = "default") {
  if (typeof window === "undefined") return null;

  const registry = window[RENDERER_REGISTRY_KEY];
  const candidates = [
    registry && registry[id],
    registry && registry.default,
    window.MarkdownRenderer,
    window.MarkdownRendererPlugin,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const plugin = normalizeRenderer(candidates[i]);
    if (plugin) return plugin;
  }
  return null;
}

/**
 * 插入 <script> 并等待加载完成；同一 URL 复用同一个 Promise。
 * @param {string} url
 * @returns {Promise<void>}
 */
export function loadRendererScript(url) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("当前环境没有 window/document，无法加载线上渲染器"));
  }

  const src = resolveScriptUrl(url);
  if (!src) {
    return Promise.reject(
      new Error("线上渲染器地址不合法：只允许 https、同源或 localhost 地址")
    );
  }

  if (scriptTasks.has(src)) return scriptTasks.get(src);

  const task = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.setAttribute("data-md-renderer", "true");

    let finished = false;

    const cleanup = () => {
      window.clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
    };

    const timer = window.setTimeout(() => {
      if (finished) return;
      finished = true;
      cleanup();
      scriptTasks.delete(src);
      if (script.parentNode) script.parentNode.removeChild(script);
      reject(new Error("线上渲染器加载超时"));
    }, SCRIPT_LOAD_TIMEOUT);

    script.onload = () => {
      if (finished) return;
      finished = true;
      cleanup();
      resolve();
    };

    script.onerror = () => {
      if (finished) return;
      finished = true;
      cleanup();
      scriptTasks.delete(src);
      if (script.parentNode) script.parentNode.removeChild(script);
      reject(new Error("线上渲染器脚本加载失败"));
    };

    document.head.appendChild(script);
  });

  scriptTasks.set(src, task);
  return task;
}

/**
 * 加载线上脚本并取回它注册的渲染器。
 * @param {string} url 脚本地址
 * @param {string} id 注册表里的 key（默认 default）
 * @returns {Promise<{ render: Function }>}
 */
export async function loadRendererPlugin(url, id = "default") {
  await loadRendererScript(url);

  const plugin = resolveRendererPlugin(id);
  if (!plugin) {
    throw new Error(
      "线上脚本已加载，但没有找到渲染器注册（window." +
        RENDERER_REGISTRY_KEY +
        "['" +
        id +
        "'] 或 window.MarkdownRenderer）"
    );
  }
  return plugin;
}
